import React, { useEffect, useState } from "react";
import { Tooltip } from "@fluentui/react-components";
import { RefreshCw } from "lucide-react";
import styles from "../ResourcesPlanner.module.scss";
import { SearchIcon, Cross, ArrowDownMedium } from "../../../modules/default-svg";
import CustomButton from "../../../components/controls/button/Button";
import Toggle from "../../../components/controls/toggle/Toggle";
import CustomPopover from "../../../components/popover/Popover";
import IconInput from "../../../components/controls/input/iconInput/IconInput";
import UserSelect from "../../../components/userSelect/UserSelect";
import ResourceDutyRosterReportButton from "../../../components/Export/ResourceDutyRosterReportButton";
import { apiCall } from "../../../store/services/service";
import { BASE_URL } from "../../../store/services/URL";
import { UserSearchRefType } from "../../../helpers/utils";
import DateField from "../../../components/controls/dateField/DateField";
import { usePlannerEngine, useUi, useView } from "../plannerContext";

const VIEW_TYPES = ["day", "week", "month"];

/**
 * The header of the planner: the project and resource filters, the report button, "Go to date", Today, the view
 * switch, the hours switch and refresh. There is no date range to page through: the timeline scrolls sideways and loads what comes into view.
 */
export default function PlannerHeader() {
  const { actions } = usePlannerEngine();
  const viewType = useView((state) => state.viewType);
  const showHours = useView((state) => state.showHours);
  const projectFilter = useUi((state) => state.projectFilter);
  const resourceFilter = useUi((state) => state.resourceFilter);

  const [projectPopoverOpen, setProjectPopoverOpen] = useState(false);
  const [projectSearch, setProjectSearch] = useState("");
  const [projects, setProjects] = useState([]);
  const [goToValue, setGoToValue] = useState(null);

  useEffect(() => {
    if (!projectPopoverOpen) return;

    const url = `${BASE_URL}search/projects${projectSearch ? `?searchTerm=${projectSearch}` : ""}`;
    apiCall
      .get({ url, isAuthToken: true })
      .then((response) => setProjects(response || []))
      .catch(() => setProjects([]));
  }, [projectSearch, projectPopoverOpen]);

  return (
    <div className={styles.header}>
      <div className={styles.headerLeft}>
        <h2 className={styles.headerTitle}>Resources Planner</h2>

        <CustomPopover
          width="320px"
          icon={
            <CustomButton
              btnClass={`${styles.filterBtn} btn_combo btn_combo_white ${projectFilter && styles.activeFilterBtn}`}
              label={projectFilter ? projectFilter.name : "All Projects"}
              clickHandler={() => setProjectPopoverOpen(!projectPopoverOpen)}
              rightIcon={<ArrowDownMedium />}
            />
          }
          status={projectPopoverOpen}
        >
          <div className="d-flex flex-column gap-3">
            <div className="d-flex justify-content-between align-items-center">
              <h6>Filter by Project</h6>
              <div onClick={() => setProjectPopoverOpen(false)} className="cursor-pointer">
                <Cross />
              </div>
            </div>
            <IconInput icon={<SearchIcon />} placeholder="Search" handleChange={setProjectSearch} />
            {projectFilter && (
              <div
                className={styles.clearFilter}
                onClick={() => {
                  actions.setProjectFilter(null);
                  setProjectPopoverOpen(false);
                }}
              >
                Clear filter
              </div>
            )}
            <div style={{ maxHeight: "200px", overflow: "auto" }}>
              <div className="d-flex flex-wrap gap-2">
                {projects
                  .filter((project) => project.id !== projectFilter?.id)
                  .map((project) => (
                    <span
                      key={project.id}
                      className={styles.filterItem}
                      onClick={() => {
                        actions.setProjectFilter({ id: project.id, name: project.name });
                        setProjectPopoverOpen(false);
                      }}
                    >
                      {project.name}
                    </span>
                  ))}
              </div>
            </div>
          </div>
        </CustomPopover>

        <div className={styles.resourceFilterWrapper}>
          <UserSelect
            className={styles.plannerResourceSelect}
            selectedUsersDisplayMode="avatar-only"
            selectedUsersDisplayLimit={6}
            multiple
            isAdminSearch
            refType={projectFilter ? UserSearchRefType.Project : null}
            refId={projectFilter ? projectFilter.id : null}
            value={resourceFilter}
            onChange={(users) => actions.setResourceFilter(users || [])}
            buttonLabel="Filter Resources"
            searchPlaceholder="Search resources"
          />
        </div>
      </div>

      <div className={styles.headerRight}>
        <ResourceDutyRosterReportButton projectId={projectFilter?.id} projectName={projectFilter?.name} resources={resourceFilter} />

        <DateField
          tooltip="Go to date"
          value={goToValue}
          onChange={(iso) => {
            setGoToValue(iso);
            if (iso) actions.goToDate(iso);
          }}
        />

        <CustomButton
          btnClass={`todayBtn ${styles.todayBtn}`}
          label="Today"
          clickHandler={() => {
            setGoToValue(null);
            actions.goToToday();
          }}
        />

        <div className={styles.viewToggle}>
          {VIEW_TYPES.map((type) => (
            <button key={type} className={`${styles.viewBtn} ${viewType === type ? styles.viewBtnActive : ""}`} onClick={() => actions.setViewType(type)}>
              {type.charAt(0).toUpperCase() + type.slice(1)}
            </button>
          ))}
        </div>

        <Toggle StyleClass={styles.hoursToggle} label="Show hours" checked={showHours} handleChange={actions.setShowHours} />

        <Tooltip content="Refresh" relationship="label" positioning="below">
          <button type="button" className={styles.refreshBtn} onClick={actions.refresh}>
            <RefreshCw size={15} />
          </button>
        </Tooltip>
      </div>
    </div>
  );
}
